import * as clipboard from 'clipboard-polyfill'
import * as React from 'react'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Time, Util} from 'yougile-util'
import {PartnerDataCompany} from 'yougile-model'
import {PageHeader} from '@components/page/page-header'
import {Backend} from '../../../common/backend'
import {PartnerClient} from '../../client/partner-client'
import {useBoolean, useInput, useListener} from '../../containers/hooks'
import {Checkbox} from '../common/base/checkbox'
import {Help} from '../common/help/help'
import {DivWithHint} from '../common/hint/hint'
import {MessageBox} from '../message-box'
import {PartnerWithdrawPopup} from './partner-withdraw-popup'

// eslint-disable-next-line max-lines-per-function
export const PartnerPage = () => {
  const bem = Util.bemFrom('partner-page')
  const [refName, changeRefName, setRefName] = useInput('')
  const [refEmail, changeRefEmail, setRefEmail] = useInput('')
  const [refPhone, changeRefPhone, setRefPhone] = useInput('')
  const [refCompany, changeRefCompany, setRefCompany] = useInput('')
  const [refError, setRefError] = React.useState('')
  const [wait, setWait] = React.useState(false)
  const withDrawOpen = useBoolean(false)
  const createCompanyWithMe = useBoolean(true)

  React.useEffect(() => {
    const id = setInterval(() => PartnerClient.fetch(), 3000)
    return () => clearInterval(id)
  }, [])

  const data = useListener(PartnerClient.listener)
  if (!data) {
    return (
      <div className={bem()}>
        <PageHeader>{Lang.msg.partner.partnerPage}</PageHeader>
      </div>
    )
  }
  const refLink = `https://yougile.com/p/${data?.refLink}`
  const copyRefLink = () => {
    clipboard
      .writeText(refLink)
      .then(() => MessageBox.show(Lang.msg.partner.refLinkCopied, 'success'))
      .catch(e => logger.log('err', e))
  }

  const companies: Array<PartnerDataCompany & {id: string}> = []
  let sum = 0
  for (const ref of ObjUtil.values(data.referred)) {
    for (const cid of ObjUtil.keys(ref.companies)) {
      const c = ref.companies[cid]
      sum += c.payAmount
      companies.push({...c, id: cid})
    }
  }
  companies.sort((a, b) => b.timestamp - a.timestamp)

  const clickVideo = () => window.open('https://youtu.be/yn0-wYuDYEk', '_blank')
  const clickOffer = () =>
    window.open('/media/docs/yougile-partner-terms-2010412.rtf', '_blank')
  const submitAddReferral = async () => {
    setRefError('')
    if (!Util.isEmail(refEmail)) {
      setRefError('E-mail неверно указан')
      return
    }
    if (!Util.isPhone(refPhone)) {
      setRefError('Телефон неверно указан')
      return
    }
    if (refName.length < 3) {
      setRefError('Укажите имя пользователя')
      return
    }
    if (refCompany.length < 3) {
      setRefError('Укажите название компании')
      return
    }

    setWait(true)
    const res = await Backend.postJSON('/data/partner/register-user', {
      ...Backend.loadCred(),
      email: refEmail,
      name: refName,
      phone: refPhone,
      company: refCompany,
      addToCompany: createCompanyWithMe.value
    }).catch(e => ({error: e as string, result: 'error'}))
    setWait(false)
    if (res.result === 'ok') {
      const okMessage = createCompanyWithMe.value
        ? 'Готово. Компания добавлена. ' +
          'Вы можете войти теперь перейти в эту компанию ' +
          'в левом меню и настроить её'
        : 'Готово. Компания добавлена'
      MessageBox.show(okMessage, 'success')
      setRefName('')
      setRefEmail('')
      setRefPhone('')
      setRefCompany('')
      PartnerClient.fetch()
    } else {
      if (res.error === 'User exists' || res.error === 'Already has partner') {
        setRefError('Пользователь с этим e-mail уже зарегистрирован')
      } else {
        MessageBox.show(
          'Неизвестная ошибка, обратитесь в поддержку или попробуйте позже',
          'danger'
        )
      }
    }
  }

  return (
    <div className={bem()}>
      {withDrawOpen.value && (
        <PartnerWithdrawPopup close={withDrawOpen.turnOff} />
      )}
      <PageHeader>{Lang.msg.partner.partnerPage}</PageHeader>

      <div className={bem('page')}>
        <div className={bem('cnt')}>
          <div className={bem('panel')}>
            <div className={bem('block')}>
              <div className={bem('block-cnt')}>
                <div className={bem('head-title')}>
                  YouGile легко рекомендовать. 94% довольны системой и около 10%
                  становятся платными клиентами
                </div>
                <div className={bem('head-explain')}>
                  <div className={bem('head-video')} onClick={clickVideo} />
                  <div className={bem('head-descr')}>
                    Как работает партнёрская программа
                  </div>
                </div>
              </div>
            </div>
            <div className={bem('block')}>
              <div className={bem('block-cnt')}>
                <div className={bem('block-main')}>
                  <div className={bem('ref-link-title')}>
                    Ваша реферальная ссылка:
                  </div>
                  <div className={bem('ref-link')} onClick={copyRefLink}>
                    {refLink}
                  </div>
                </div>
                <div className={bem('explain')}>
                  Попробуйте найти и отправить ссылку 10 руководителям средних
                  команд. Прогнозируем: 8 переходов, 4-5 плотных тестов, 1
                  оплата со средним чеком 70 тыс. Ваш доход около 10-15 тыс.
                </div>
              </div>
            </div>

            <div className={bem('block')}>
              <div className={bem('manual-title')}>
                Зарегистрировать клиента вручную (будет закреплён за вами)
              </div>
              <div className={bem('block-cnt')}>
                <div className={bem('manual')}>
                  <div className={bem('block-main', {form: true})}>
                    <form autoComplete="off">
                      <input
                        className={bem('input')}
                        type="text"
                        placeholder="Имя клиента"
                        value={refName}
                        onChange={changeRefName}
                        autoComplete="new-password"
                      />
                      <input
                        className={bem('input')}
                        type="text"
                        placeholder="E-mail клиента"
                        value={refEmail}
                        onChange={changeRefEmail}
                        autoComplete="new-password"
                      />
                      <input
                        className={bem('input')}
                        type="text"
                        placeholder="Телефон клиента"
                        value={refPhone}
                        onChange={changeRefPhone}
                        autoComplete="new-password"
                      />
                      <input
                        className={bem('input')}
                        type="text"
                        placeholder="Название компании"
                        value={refCompany}
                        onChange={changeRefCompany}
                        autoComplete="new-password"
                      />
                    </form>

                    <div
                      className={bem('manual-checkbox')}
                      onClick={createCompanyWithMe.toggle}
                    >
                      <Checkbox
                        value={createCompanyWithMe.value}
                        text="Добавить себя в новую компанию в качестве администратора"
                      />
                    </div>
                    {!!refError && (
                      <div className={bem('error')}>{refError}</div>
                    )}

                    {wait ? (
                      <div className={bem('loading')} />
                    ) : (
                      <div className={bem('manual-btn-cnt')}>
                        <div
                          className={bem('manual-btn')}
                          onClick={submitAddReferral}
                        >
                          Зарегистрировать
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={bem('explain')}>
                  Будет создана новая компания и ваш клиент будет в ней
                  администратором. Регистрационные данные будут высланы на
                  указанный e-mail с приглашением активировать регистрацию.
                  <br />
                  Вы можете добавить себя администратором и помочь клиенту с
                  настройками системы.
                  <br />
                  <br />
                  Компания и пользователь по партнерской программе будут
                  закреплены за вами.
                </div>
              </div>
            </div>

            <div className={bem('block')}>
              <div className={bem('subtitle')}>Статистика</div>
              <div className={bem('actions')}>
                <div
                  className={bem('action-item')}
                  onClick={withDrawOpen.turnOn}
                >
                  Вывести
                </div>
                <div className={bem('action-item')} onClick={clickOffer}>
                  Договор
                </div>
              </div>

              <div className={bem('row')}>
                <div className={bem('col')}>Досупно для вывода</div>
                <div className={bem('col')}>
                  <div className={bem('amount')}>
                    {Math.round(sum * 0.2) - data.withdrawn} руб.
                  </div>
                </div>
              </div>

              <div className={bem('row')}>
                <div className={bem('col')}>Выведено</div>
                <div className={bem('col')}>
                  <div className={bem('amount')}>{data.withdrawn} руб.</div>
                </div>
              </div>

              <div className={bem('row')}>
                <div className={bem('col')}>Перешло по ссылке</div>
                <div className={bem('col')}>{data.siteVisitors}</div>
              </div>

              <div className={bem('row')}>
                <div className={bem('col')}>Зарегистрировалось</div>
                <div className={bem('col')}>
                  {Object.keys(data.referred).length}
                </div>
              </div>

              <div className={bem('row')}>
                <div className={bem('col')}>Компаний зарегистрировано</div>
                <div className={bem('col')}>{companies.length}</div>
              </div>

              <div className={bem('row')}>
                <div className={bem('col')}>Заработано</div>
                <div className={bem('col')}>{Math.round(sum * 0.2)} руб.</div>
              </div>
            </div>

            <div className={bem('block')}>
              <div className={bem('subtitle')}>
                Список компаний рефералов <Help name="partnerReferrals" />
              </div>
              {companies.length === 0 ? (
                <div className={bem('no-refs')}>Пока что нет рефералов</div>
              ) : (
                <>
                  <div className={bem('ref-header')}>
                    <DivWithHint
                      content="Дата создания компании"
                      className={bem('ref-col')}
                    >
                      Дата создания
                    </DivWithHint>
                    <DivWithHint
                      content="Название компании"
                      className={bem('ref-col')}
                    >
                      Название
                    </DivWithHint>
                    <DivWithHint
                      content="Источник перехода пользователя, который создал компанию"
                      className={bem('ref-col')}
                    >
                      Источник
                    </DivWithHint>
                    <DivWithHint
                      content="Всего пользователей в компании"
                      className={bem('ref-col')}
                    >
                      Пользователей
                    </DivWithHint>
                    <DivWithHint
                      content="Количество задач за 7 дней"
                      className={bem('ref-col')}
                    >
                      Задач/7д
                    </DivWithHint>
                    <DivWithHint
                      content="Количество сообщений за 7 дней"
                      className={bem('ref-col')}
                    >
                      Сообщ/7д
                    </DivWithHint>
                    <DivWithHint
                      content="Сумма оплат лицензий для этой компании. 20% этой суммы будет доступно вам для вывода"
                      className={bem('ref-col')}
                    >
                      Сумма оплат
                    </DivWithHint>
                  </div>
                  <div>
                    {companies.map(company => {
                      const stat = PartnerClient.stats[company.id]
                      return (
                        <div key={company.id} className={bem('ref')}>
                          <div className={bem('ref-col')}>
                            {Time.formatDate(company.timestamp)}
                          </div>
                          <DivWithHint
                            content={company.displayAs}
                            className={bem('ref-col')}
                          >
                            {company.displayAs}
                          </DivWithHint>
                          <DivWithHint
                            content={stat?.source ?? ''}
                            className={bem('ref-col')}
                          >
                            {stat?.source ?? ''}
                          </DivWithHint>
                          <div className={bem('ref-col')}>
                            {stat?.allUsers ?? ''}
                          </div>
                          <div className={bem('ref-col')}>
                            {stat?.tasks1w ?? ''}
                          </div>
                          <div className={bem('ref-col')}>
                            {stat?.msg1w ?? ''}
                          </div>
                          <div className={bem('ref-col')}>
                            <b>{company.payAmount}</b>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>

            <div className={bem('footer')}>
              По всем вопросам обращайтесь{' '}
              <a href="mailto:partner@yougile.com" target="_blank">
                partner@yougile.com
              </a>
              . Оставляйте телефон, если нужно что-то обсудить
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
